import { useQuery } from "@apollo/client";
import { Delete, Edit } from "@mui/icons-material";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
} from "@mui/material";
import { ReactNode } from "react";

import {
  DeleteAudienceContactFieldMutation,
  GetAudienceContactFieldsQuery,
  GetAudienceContactFieldsQueryVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import Table from "~frontend/components/Table";
import {
  DELETE_AUDIENCE_CONTACT_FIELD,
  GET_AUDIENCE_CONTACT_FIELDS,
} from "~frontend/graphql/audience";
import { useFeatureFlag } from "~frontend/providers/LoggedInUserProvider/context";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import useMutation from "~frontend/util/useMutation";

import { sortContactFields } from "./util";

export const FieldTypesByDbType = {
  STRING: {
    label: "Text",
    type: "STRING",
    typeParameters: {
      maxLength: 255,
    },
  },
  BOOLEAN: {
    label: "True/False",
    type: "BOOLEAN",
  },
  NUMBER: {
    label: "Number",
    type: "NUMBER",
  },
  ENUM: {
    label: "Single Select",
    type: "ENUM",
    typeParameters: {
      values: [""],
    },
  },
  STATE: {
    label: "State",
    type: "STATE",
  },
  ZIP: {
    label: "Zip Code",
    type: "ZIP",
  },
};

const tableSx = {
  ".MuiTableCell-root": {
    borderBottom: `1px solid ${palette.lightGray}`,
    py: 1,
  },
};

export const ViewFields = ({
  opnId,
  setEditingField,
  onCancel,
}: {
  opnId: string;
  setEditingField: (
    field:
      | GetAudienceContactFieldsQuery["getAudienceContactFields"][number]
      | "NEW"
      | null
  ) => void;
  onCancel: () => void;
}): ReactNode => {
  const customFieldsAllowed = useFeatureFlag("audienceCustomFields");
  const { subaccount } = useSubaccount()!;
  const {
    data: fieldsData,
    loading,
    error,
  } = useQuery<
    GetAudienceContactFieldsQuery,
    GetAudienceContactFieldsQueryVariables
  >(GET_AUDIENCE_CONTACT_FIELDS, {
    variables: {
      input: { opnId, subaccountId: subaccount.id },
    },
  });

  const [deleteContactField] = useMutation<DeleteAudienceContactFieldMutation>(
    DELETE_AUDIENCE_CONTACT_FIELD,
    {
      refetchQueries: [GET_AUDIENCE_CONTACT_FIELDS],
      awaitRefetchQueries: true,
    }
  );

  return (
    <FlexBox
      direction="column"
      gap={3}
      data-testid="manage-custom-fields-modal"
    >
      <Typography variant="h1">Manage Custom Fields</Typography>
      <Card>
        <CardHeader title="Standard Fields" />
        <CardContent sx={{ px: 2, py: 1 }}>
          <AsyncWrapper data={fieldsData} loading={loading} errors={error}>
            {data => (
              <Table<
                GetAudienceContactFieldsQuery["getAudienceContactFields"][number]
              >
                size="small"
                columns={[
                  {
                    title: "Field Name",
                    key: "label",
                    sx: { width: "180px" },
                    renderValue: field => field.displayName || field.name,
                  },
                  {
                    title: "Field Type",
                    key: "type",
                    renderValue: field =>
                      FieldTypesByDbType[field.type]?.label || field.type,
                    align: "left",
                  },
                ]}
                rows={sortContactFields(
                  data.getAudienceContactFields.filter(
                    field => !field.isEditable
                  )
                )}
                containerSx={tableSx}
                noFooter
              />
            )}
          </AsyncWrapper>
        </CardContent>
      </Card>

      {customFieldsAllowed && (
        <Card
          sx={{ border: 0, backgroundColor: palette.lightestGray }}
          data-testid="custom-fields-card"
        >
          <CardHeader title="Custom Fields" />
          <CardContent sx={{ px: 2, py: 1 }}>
            <AsyncWrapper data={fieldsData} loading={loading} errors={error}>
              {data => (
                <Table<
                  GetAudienceContactFieldsQuery["getAudienceContactFields"][number]
                >
                  rowActions={[
                    {
                      id: "edit-field",
                      type: "button",
                      icon: <Edit />,
                      tooltipLabel: "Edit field",
                      onClick: ({ id }) => {
                        const field = data.getAudienceContactFields.find(
                          field => field.id === id
                        );
                        if (field && field.isEditable) {
                          setEditingField(field);
                        }
                      },
                    },
                    {
                      id: "delete-field",
                      type: "button",
                      icon: <Delete />,
                      tooltipLabel: "Delete field",
                      confirm: {
                        title: "Delete Custom Field",
                        description:
                          "Are you sure you want to delete this custom field?",
                      },
                      onClick: async ({ id }) => {
                        await deleteContactField({
                          variables: {
                            input: { id, subaccountId: subaccount.id },
                          },
                        });
                      },
                    },
                  ]}
                  size="small"
                  columns={[
                    {
                      title: "Field Name",
                      key: "name",
                      sx: { width: "180px" },
                      renderValue: field => field.name,
                    },
                    {
                      title: "Field Type",
                      key: "type",
                      renderValue: field =>
                        FieldTypesByDbType[field.type]?.label || field.type,
                      align: "left",
                    },
                  ]}
                  rows={sortContactFields(
                    data.getAudienceContactFields.filter(
                      field => field.isEditable
                    )
                  )}
                  noFooter
                  emptyState="No custom fields found."
                  actionSx={{ ".MuiButtonBase-root": { py: "0", px: "6px" } }}
                  containerSx={tableSx}
                />
              )}
            </AsyncWrapper>
          </CardContent>
        </Card>
      )}
      <FlexBox justifyContent="flex-end" gap={1}>
        <Button
          variant="contained"
          color="primary"
          onClick={() => setEditingField("NEW")}
        >
          Create Custom Field
        </Button>
        <Button variant="contained" color="tertiary" onClick={onCancel}>
          Close
        </Button>
      </FlexBox>
    </FlexBox>
  );
};
